'use client';

import React, { useState } from 'react';
import { Filter, ChevronDown, ChevronUp, X } from 'lucide-react';

const categories = [
  { id: 'cat-all', label: 'All Schools', count: 19 },
  { id: 'school-practice', label: 'School of Property Practice', count: 5 },
  { id: 'school-real-estate', label: 'School of Real Estate', count: 4 },
  { id: 'school-development', label: 'School of Property Development', count: 3 },
  { id: 'school-finance', label: 'School of Property Finance', count: 2 },
  { id: 'school-cities', label: 'School of Cities & Infrastructure', count: 8 },
];

const nqfLevels = [
  { id: 'nqf-4', label: 'NQF 4', count: 1 },
  { id: 'nqf-5', label: 'NQF 5', count: 1 },
  { id: 'nqf-6', label: 'NQF 6 Pathway', count: 1 },
];

const formats = [
  { id: 'fmt-online', label: 'Online Self-Paced' },
  { id: 'fmt-hybrid', label: 'Online + Intensives' },
  { id: 'fmt-live', label: 'Online + Live Sessions' },
];

const durations = [
  { id: 'dur-short', label: 'Under 6 months' },
  { id: 'dur-mid', label: '6–12 months' },
  { id: 'dur-long', label: '12+ months' },
];

interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function FilterSection({ title, children, defaultOpen = true }: FilterSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border pb-4 mb-4 last:border-0 last:mb-0 last:pb-0">
      <button
        className="flex items-center justify-between w-full mb-3"
        onClick={() => setOpen(!open)}
      >
        <span className="font-sans text-sm font-semibold text-foreground">{title}</span>
        {open ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
      </button>
      {open && children}
    </div>
  );
}

interface MarketplaceFiltersProps {
  onFilterChange?: (filters: Record<string, unknown>) => void;
}

export default function MarketplaceFilters({ onFilterChange }: MarketplaceFiltersProps) {
  const [selectedCategory, setSelectedCategory] = useState('cat-all');
  const [selectedNqf, setSelectedNqf] = useState<string[]>([]);
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [selectedDurations, setSelectedDurations] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 50000]);

  const toggleMulti = (
    id: string,
    current: string[],
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setter(current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
  };

  const activeFilterCount =
    (selectedCategory !== 'cat-all' ? 1 : 0) +
    selectedNqf.length +
    selectedFormats.length +
    selectedDurations.length;

  const clearAll = () => {
    setSelectedCategory('cat-all');
    setSelectedNqf([]);
    setSelectedFormats([]);
    setSelectedDurations([]);
    setPriceRange([0, 50000]);
  };

  return (
    <aside className="w-64 flex-shrink-0 hidden lg:block">
      <div className="bg-card rounded-2xl border border-border luxury-shadow p-5 sticky top-8">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Filter size={15} className="text-primary" />
            <span className="font-sans font-semibold text-sm text-foreground">Filters</span>
            {activeFilterCount > 0 && (
              <span className="bg-primary text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </div>
          {activeFilterCount > 0 && (
            <button
              onClick={clearAll}
              className="flex items-center gap-1 font-sans text-xs text-muted-foreground hover:text-danger transition-colors"
            >
              <X size={12} />
              Clear all
            </button>
          )}
        </div>

        <FilterSection title="Category">
          <div className="space-y-1">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-primary text-white' :'hover:bg-secondary text-muted-foreground hover:text-foreground'
                }`}
              >
                <span className="font-sans text-sm">{cat.label}</span>
                <span className={`font-sans text-xs ${selectedCategory === cat.id ? 'text-white/70' : 'text-muted-foreground'}`}>
                  {cat.count}
                </span>
              </button>
            ))}
          </div>
        </FilterSection>

        <FilterSection title="NQF Level">
          <div className="space-y-2">
            {nqfLevels.map((nqf) => (
              <label key={nqf.id} className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedNqf.includes(nqf.id)}
                  onChange={() => toggleMulti(nqf.id, selectedNqf, setSelectedNqf)}
                  className="w-4 h-4 rounded accent-primary cursor-pointer"
                />
                <span className="font-sans text-sm text-foreground group-hover:text-primary transition-colors flex-1">
                  {nqf.label}
                </span>
                <span className="font-sans text-xs text-muted-foreground">{nqf.count}</span>
              </label>
            ))}
          </div>
        </FilterSection>

        <FilterSection title="Study Format">
          <div className="space-y-2">
            {formats.map((fmt) => (
              <label key={fmt.id} className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedFormats.includes(fmt.id)}
                  onChange={() => toggleMulti(fmt.id, selectedFormats, setSelectedFormats)}
                  className="w-4 h-4 rounded accent-primary cursor-pointer"
                />
                <span className="font-sans text-sm text-foreground group-hover:text-primary transition-colors">
                  {fmt.label}
                </span>
              </label>
            ))}
          </div>
        </FilterSection>

        <FilterSection title="Duration" defaultOpen={false}>
          <div className="space-y-2">
            {durations.map((dur) => (
              <label key={dur.id} className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={selectedDurations.includes(dur.id)}
                  onChange={() => toggleMulti(dur.id, selectedDurations, setSelectedDurations)}
                  className="w-4 h-4 rounded accent-primary cursor-pointer"
                />
                <span className="font-sans text-sm text-foreground group-hover:text-primary transition-colors">
                  {dur.label}
                </span>
              </label>
            ))}
          </div>
        </FilterSection>

        <FilterSection title="Price Range (ZAR)" defaultOpen={false}>
          <div className="space-y-3">
            <input
              type="range"
              min={0}
              max={50000}
              step={500}
              value={priceRange[1]}
              onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
              className="w-full accent-primary"
            />
            <div className="flex items-center justify-between">
              <span className="font-sans text-xs text-muted-foreground">R 0</span>
              <span className="font-sans text-xs font-semibold text-primary">
                Up to R {priceRange[1].toLocaleString()}
              </span>
            </div>
          </div>
        </FilterSection>
      </div>
    </aside>
  );
}